import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)) newErrors.email = 'Valid email is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);

      // Replace these with your actual EmailJS IDs
      const serviceID = 'service_ne6qnnx';
      const templateID = 'template_2egrltl';
      const publicKey = 'EIJ9uuFXCIiT5OIN3';

      // Send the email using EmailJS
      emailjs.send(
        serviceID,
        templateID,
        {
          name: formData.name,
          email: formData.email,
          message: formData.message
        },
        publicKey
      )
        .then((response) => {
          console.log('Email sent successfully!', response.status, response.text);
          setIsSubmitted(true);
          setFormData({ name: '', email: '', message: '' });
        })
        .catch((error) => {
          console.error('Failed to send email:', error);
          setErrors({ ...errors, form: 'Failed to send message. Please try again later.' });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <div className="mt-[80px] bg-gray-100 min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Contact Form Section */}
        <div className="bg-white p-8 rounded-2xl shadow-2xl transform transition-all duration-500 hover:shadow-[0_15px_30px_rgba(34,197,94,0.4)]">
          <h2 className="text-4xl font-extrabold text-center bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-emerald-600 mb-4">
            Reach Out to Us
          </h2>
          <p className="text-gray-600 text-center mb-8 font-medium">
            Have questions about our agricultural products? Drop us a message!
          </p>

          {isSubmitted && (
            <div className="p-4 mb-6 text-sm text-green-700 bg-green-100 rounded-lg">
              Thank you! Your message has been sent successfully. We'll get back to you soon.
            </div>
          )}

          {errors.form && (
            <div className="p-4 mb-6 text-sm text-red-700 bg-red-100 rounded-lg">
              {errors.form}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-300 hover:border-green-500 ${errors.name ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="Your Name"
                  aria-label="Your Name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-300 hover:border-green-500 ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="Your Email"
                  aria-label="Your Email"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg bg-gray-50 text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-300 hover:border-green-500 ${errors.message ? 'border-red-500' : 'border-gray-200'}`}
                  placeholder="Your Message"
                  aria-label="Your Message"
                ></textarea>
                {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white p-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transform hover:scale-105 transition-all duration-300 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Contact Info & Map Section */}
        <div className="space-y-8">
          <div className="bg-white p-8 rounded-2xl shadow-2xl transform transition-all duration-500 hover:shadow-[0_15px_30px_rgba(34,197,94,0.4)]">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Contact Information</h3>
            <div className="space-y-4 text-gray-600">
              <p className="flex items-center">
                <svg className="w-6 h-6 mr-2 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                info@pcds.co.in
              </p>

              {/* Added Mobile Number */}
              <p className="flex items-center">
                <svg className="w-6 h-6 mr-2 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
                +91 9234041819
              </p>
              
              <p className="flex items-start">
                <svg className="w-6 h-6 mr-2 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span>
                  1st Floor Mukta Tawar,Near Andheri Station,<br />
                  Mumbai - 400058<br />
                </span>
              </p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-2xl transform transition-all duration-500 hover:shadow-[0_15px_30px_rgba(34,197,94,0.4)]">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Follow Us</h3>
            <div className="flex space-x-4">
           
              <a href="https://www.facebook.com/profile.php?id=61575573322605" className="text-green-600 hover:text-green-700 transform hover:scale-110 transition-all duration-300" aria-label="Facebook">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd"></path>
                </svg>
              </a>

              {/* Added YouTube Link */}
              <a href="https://www.youtube.com/@Sakshikumari-i2b" className="text-green-600 hover:text-green-700 transform hover:scale-110 transition-all duration-300" aria-label="YouTube">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
             
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-2xl transform transition-all duration-500 hover:shadow-[0_15px_30px_rgba(34,197,94,0.4)]">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Our Location</h3>
            <div className="h-64 rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.715607416885!2d72.83821431537794!3d19.05272245796736!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c8df05ccf9c5%3A0x3d1a8f2a3e4e4b1d!2sAndheri%20Station!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Company Location"
                aria-label="Google Maps showing company location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;